import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  ThanksPrintConfig,
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';

import { GiftThanksPrintPreview } from './GiftThanksPrintPreview';
import { GiftThanksPrintSection } from './GiftThanksPrintSection';
import { giftThanksPrintAtom } from '../../state/giftThanksPrint.atom';
import {
  composeThanksSender,
  normalizeThanksPreset,
  parseThanksSender,
  templateMessagePresets,
  thanksSenderNameLimit,
} from '../../utils/thanksPrintMessage';

interface GiftThanksPrintSummaryProps {
  /** 통일 적용할 감사인쇄 설정 (대상 상품 중 대표 브랜드) */
  thanksPrint: ThanksPrintConfig<Image>;
  brandId: string;
  bugoId: string | null;
  /** 대상 상품들이 허용하는 템플릿 _id 목록 (미지정 = 브랜드 전체 허용) */
  allowedTemplateIds?: string[];
  /** 감사 인쇄 대상 상품 수 (통일 안내 문구용) */
  eligibleCount: number;
}

/** 편집 모달 — 상세페이지와 동일한 GiftThanksPrintSection을 그대로 재사용 */
const GiftThanksPrintEditModal: FC<{
  thanksPrint: ThanksPrintConfig<Image>;
  brandId: string;
  bugoId: string | null;
  allowedTemplateIds?: string[];
  title: string;
  onClose: () => void;
}> = function GiftThanksPrintEditModal({
  thanksPrint,
  brandId,
  bugoId,
  allowedTemplateIds,
  title,
  onClose,
}) {
  // 모달 열려있는 동안 배경 스크롤 잠금
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 (sticky) */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-4 py-3">
          <h3 className="text-base font-bold text-gray-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-stone-700 px-4 py-1.5 text-sm font-bold text-white active:bg-stone-800"
          >
            완료
          </button>
        </div>
        {/* 본문 (스크롤) — 입력값은 atom에 자동 저장되어 닫으면 요약에 바로 반영됨 */}
        <div className="flex-1 overflow-y-auto">
          <GiftThanksPrintSection
            thanksPrint={thanksPrint}
            brandId={brandId}
            bugoId={bugoId}
            allowedTemplateIds={allowedTemplateIds}
            embedded
          />
        </div>
      </div>
    </div>
  );
};

/**
 * 주문/결제 화면용 감사 메시지 인쇄 "축약형" 요약.
 * - 상세페이지에서 깜빡하고 넘어간 경우에도 여기서 작성/수정 가능.
 * - 감사 메시지는 주문 전체에 하나로 통일 적용된다는 점을 안내.
 * - 적용 완료된 내용은 미니 미리보기로 보여주고, [수정] 시 상세페이지와 동일한 편집 UI(모달)를 띄움.
 */
export const GiftThanksPrintSummary: FC<GiftThanksPrintSummaryProps> =
  function GiftThanksPrintSummary({
    thanksPrint,
    brandId,
    bugoId,
    allowedTemplateIds,
    eligibleCount,
  }) {
    const selection = useRecoilValue(giftThanksPrintAtom);
    const [editing, setEditing] = useState(false);

    // 노출 템플릿 (enabled !== false ∩ 상품별 허용 목록, order 순)
    const templates = useMemo<ThanksPrintTemplate<Image>[]>(() => {
      const allowed =
        allowedTemplateIds && allowedTemplateIds.length > 0
          ? new Set(allowedTemplateIds)
          : null;
      return (thanksPrint.templates ?? [])
        .filter(
          (t) => t.enabled !== false && (!allowed || (t._id && allowed.has(t._id))),
        )
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [thanksPrint.templates, allowedTemplateIds]);

    // 미리보기용 템플릿 (같은 브랜드 + 저장된 디자인 우선, 없으면 첫 템플릿)
    const previewTemplate = useMemo(() => {
      if (selection?.brandId === brandId && selection.templateId) {
        const found = templates.find((t) => t._id === selection.templateId);
        if (found) return found;
      }
      return templates[0];
    }, [templates, selection, brandId]);

    const applied = !!selection && selection.message.trim().length > 0;

    // 실제 인쇄값과 동일하게 보정한 미리보기 감사글
    // (지정 문구 템플릿이면 보존된 자유입력 대신 세트 문구를 보여줌 — DTO 정규화와 동일)
    const previewMessage = useMemo(() => {
      const message = selection?.message ?? '';
      const presets = templateMessagePresets(previewTemplate);
      if (presets.length === 0) return message;
      return (
        presets.find(
          (p) => normalizeThanksPreset(p) === normalizeThanksPreset(message),
        ) ??
        presets[0] ??
        ''
      );
    }, [previewTemplate, selection]);

    const sender = useMemo(() => {
      if (!selection) return '';
      const parsed = parseThanksSender(selection.senderName, selection.senderSuffix);
      // 템플릿 이름 글자수 제한 반영 (DTO와 동일하게 잘라 미리보기 = 인쇄 결과)
      const limit = thanksSenderNameLimit(previewTemplate);
      const name =
        limit !== undefined ? parsed.name.slice(0, limit) : parsed.name;
      return composeThanksSender(name, parsed.suffix);
    }, [selection, previewTemplate]);

    return (
      <div className="bg-white p-4">
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-100 text-sm">
            ✉️
          </span>
          <h3 className="font-bold text-gray-800">감사 메시지 인쇄</h3>
        </div>

        {/* 통일 안내 — 대상 상품이 2개 이상일 때 노출 */}
        {eligibleCount > 1 && (
          <p className="mb-3 break-keep rounded-lg bg-stone-50 px-3 py-2 text-xs leading-relaxed text-stone-600">
            감사 메시지는 주문 전체에 <b className="text-stone-800">하나로 통일</b>되어
            인쇄돼요. (감사 인쇄 상품 {eligibleCount}개 공통 적용)
          </p>
        )}

        {applied ? (
          <div>
            {/* 완성된 감사 인쇄 미리보기 (한 장 크게) */}
            <GiftThanksPrintPreview
              template={previewTemplate}
              message={previewMessage}
              sender={sender}
            />
            {/* 수정하고 싶을 때 */}
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-2.5 w-full rounded-lg border border-gray-300 py-2.5 text-sm font-bold text-gray-600 active:bg-gray-100"
            >
              감사 메시지 수정하기
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex w-full items-center justify-between gap-2 rounded-xl border border-dashed border-stone-300 bg-stone-50/60 px-4 py-3 text-left"
          >
            <span className="break-keep text-sm text-stone-600">
              아직 작성하지 않으셨어요.{' '}
              <b className="text-stone-800">감사 메시지를 작성</b>해 함께 보내보세요.
            </span>
            <span className="shrink-0 rounded-lg bg-stone-700 px-3 py-1.5 text-xs font-bold text-white">
              작성하기
            </span>
          </button>
        )}

        {editing && (
          <GiftThanksPrintEditModal
            thanksPrint={thanksPrint}
            brandId={brandId}
            bugoId={bugoId}
            allowedTemplateIds={allowedTemplateIds}
            title={applied ? '감사 메시지 수정' : '감사 메시지 작성'}
            onClose={() => setEditing(false)}
          />
        )}
      </div>
    );
  };
